// Central config for the in-app Tutorial / Help system.
// Each topic: id, title, category (for grouping in the Help tab), an illustrative image, and
// body (array of short text lines/bullets).
export const HELP_TOPICS = [
  {
    id: "map_creation",
    category: "Maps & Scenes",
    title: "Setting Up Your Map",
    image: "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/1d5024d35c53afd7e2fdab8bf8fb85ba100a9651249d542eae538addb3c24159.jpeg",
    body: [
      "Upload a battle map image from the Scene tab's \"Upload Map Image\" button — any PNG or JPG works.",
      "Use the Grid Square Size slider to line your map's grid up with the table's grid overlay (5ft per square by default).",
      "Add multiple scenes to one game using the tabs at the top of the map — great for multi-room dungeons.",
      "Draw a Door between two scenes so players can walk from one map straight into another.",
      "Toggle Fog of War and Dynamic Lighting for the whole game, or flip \"Full Sight\" on a specific scene so everyone sees the whole room (handy for shops or social scenes).",
      "Weather and Darkness dropdowns add atmosphere without needing extra art.",
    ],
  },
  {
    id: "npc_setup",
    category: "NPCs & Monsters",
    title: "Adding & Running NPCs",
    image: "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/b96dfd96ee5aeb9353c4a95406b9f17037c4dea2a2a8928dd97b8a153b684ff9.jpeg",
    body: [
      "Open the NPCs tab and hit \"Add NPC / Enemy Token\" to spawn a monster onto the current scene.",
      "Give the NPC attacks (name, bonus, range, damage) so the built-in Combat AI can act for it automatically during combat.",
      "Turn on Auto-Attack if you want the NPC to strike on its own turn once a target is in range — leave it off if you'd rather approve every attack yourself.",
      "Select any NPC token to set HP, portrait, and an optional sound effect that plays on its turn.",
      "In Free Roam, placing an NPC near players can spontaneously trigger an encounter, prompting nearby players to join initiative.",
    ],
  },
  {
    id: "combat_tracker",
    category: "Combat",
    title: "Running the Combat Tracker",
    image: "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/aea14b801d154e9e2991d1d78f813a2f85d8e53777b1d9ce4a544ddfa6ecdbe5.jpeg",
    body: [
      "Open the Combat Tracker (bottom of the GM Sidebar's Scene tab) to set up initiative before a fight starts.",
      "Every PC/NPC on the current scene is added to the roster automatically — remove any you don't want with the X, or add any other token with the picker.",
      "Type an initiative number for each combatant, then hit Start Combat to lock in turn order (highest first).",
      "Once active, use Next Turn to advance — you can still add or remove combatants mid-fight without disturbing whose turn it currently is.",
      "End Combat posts an auto-generated summary of attacks, damage, and the top damage-dealer straight to the chat log.",
    ],
  },
  {
    id: "free_roam",
    category: "Free Roam",
    title: "Exploring in Free Roam",
    image: "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/c026f259fd6000896aa1ac8eda6961757a106c1bfebb017d46d5579b115b32bb.jpeg",
    body: [
      "Free Roam lets players explore between big story beats without a strict turn order — flip the switch in the Scene tab to turn it on.",
      "An in-game clock ticks forward as players move; \"Hustle\" pace covers ground twice as fast but drains a stamina timer.",
      "Attacking an NPC (or getting spotted) spins up a spontaneous encounter — only the players nearby get prompted to join initiative, not the whole table.",
      "Pull any NPC into an ongoing encounter mid-fight from this panel, and hit Next Turn / End whenever it wraps up.",
      "Turning Free Roam off returns the table to standard scene-by-scene Campaign play.",
    ],
  },
  {
    id: "vision_lighting",
    category: "Maps & Scenes",
    title: "Fog of War, Lighting & Darkness",
    image: "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/6b7f6209cb639a982f931e758387266ae1d4ce34f20f6dda8a30c1c2fcd63f0a.jpeg",
    body: [
      "Fog of War hides unexplored parts of the map until a player's token has actually seen them.",
      "Dynamic Lighting restricts sight to torches, lanterns, and each character's own vision — no more seeing through walls.",
      "Darkness (Non-Magical / Magical) dims a scene further; only darkvision or \"See in Darkness\" tokens can see through Magical Darkness.",
      "Flip \"Full Sight\" on a specific scene to bypass all of the above for everyone — perfect for shops, taverns, or cutscene-style rooms.",
      "Add a Light Source (Scene tab) to drop a glowing campfire/lantern token that lights an area without being a full NPC.",
    ],
  },
  {
    id: "map_tools",
    category: "Maps & Scenes",
    title: "The Map Toolbar (Templates, Walls, Doors & Traps)",
    image: "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/4a62805e64dbe8223842f34c73060da89638a161f7b73786fd982d19a0586de8.jpeg",
    body: [
      "The floating toolbar at the bottom of the map holds Measure, plus AOE templates (Burst/Cone/Line) for spells and abilities — click a shape, then click or drag on the map to place it.",
      "GM-only tools add Obstacle/Trap (a hidden trigger that fires when a token crosses it), Trigger Line (a chain-drawn tripwire), Wall (blocks line of sight), and Door.",
      "Doors can link two different scenes — walking through one on the map moves the token straight onto the linked map.",
      "Any placed template or obstacle can be clicked to select it, then removed with the red X that appears.",
      "Toggle a tool off by clicking it again, or hit the X that appears next to the toolbar.",
    ],
  },
  {
    id: "raid_campaign_clock",
    category: "Raids & Alliances",
    title: "The Shared Campaign Clock",
    body: [
      "In a Raid, every Branch shares the exact same in-world clock — it's not tied to any one table's own Free Roam movement.",
      "Only the Master GM can set or advance the time (hour/minute/day fields, or the +1 Hour / +6 Hours / +1 Day quick buttons).",
      "Generals see the clock too, read-only, so everyone stays in sync on what time it is in the story — no Branch can drift ahead or behind another.",
      "This works whether or not Free Roam itself is turned on for that table — the clock is always visible and controllable from the Scene tab for the Master GM.",
    ],
  },
];

export function getHelpTopic(id) {
  return HELP_TOPICS.find((t) => t.id === id);
}

